import { Column } from "../../../../../core/components/Column";
import { TextComponent } from "../../../../../core/components/TextComponent";
import { CustomButton } from "../../../../../core/components/CustomButton";
import { Container } from "../../../../../core/components/Container";
import { ErrorMessage, Field, Form, Formik, FormikHelpers } from "formik";
import { DescriptionFormValues, descriptionValidationSchema } from "../../interfaces/DescriptionFormInterface";
import { deleteUserInfo } from "../../store/OnboardingSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../../core/store/store";
import { onGetUser, onSendProfileConfig, onUpdateUser } from "../../store/OnboardingThunk";
import { useNavigate } from "react-router-dom";
import { useAlert } from "../../../../../core/hooks/useAlert";
import { LoadingComponent } from "../../../../../core/components/LoadingComponent";
import { useState } from "react";
import { buildDescriptionUserPayload } from "./descriptionPayload";

const initialValues: DescriptionFormValues = {
    description: "",
};

export function Description() {
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const dispatch: AppDispatch = useDispatch();
    const { userInfo, profileConfig, isUpdateLoading } = useSelector((state: RootState) => state.onboarding);
    const maxLengthBIO = 1000;
    const [submitError, setSubmitError] = useState("");

    const handleSubmit = async (values: DescriptionFormValues, { setSubmitting }: FormikHelpers<DescriptionFormValues>) => {
        setSubmitError("");
        const latestUserResponse = await dispatch(onGetUser());
        const latestUser = onGetUser.fulfilled.match(latestUserResponse)
            ? latestUserResponse.payload
            : undefined;
        const userPayload = buildDescriptionUserPayload({
            userInfo,
            latestUser,
            description: values.description || "",
        });
        const requests = [dispatch(onUpdateUser({ data: userPayload }))];
        if (profileConfig) {
            requests.push(dispatch(onSendProfileConfig({ data: profileConfig })) as any);
        }
        const responses = await Promise.all(requests);
        if (responses.every((response) => response.meta.requestStatus === "fulfilled")) {
            dispatch(deleteUserInfo());
            navigate('/');
        } else {
            const rejectedResponse = responses.find(
                (response) => response.meta.requestStatus === "rejected"
            );
            const errorMessage =
                rejectedResponse && "error" in rejectedResponse && rejectedResponse.error?.message
                    ? rejectedResponse.error.message
                    : "Something went wrong, try again later";
            setSubmitError(errorMessage);
            showAlert({
                type: "error",
                message: errorMessage,
            });
        }
        setSubmitting(false);
    };

    return (
    <LoadingComponent isLoading={isUpdateLoading}>
        <Column crossAxisAlignment="center">
        <Column
            crossAxisAlignment="center"
            className="md:bg-white px-10 pb-10 md:my-10 md:p-20 self-center text-center w-full md:w-[584px] lg:shadow-xl rounded-lg gap-5"
        >
            <Container>
                <TextComponent variant="h2" className="font-bold">
                    Description
                </TextComponent>
                <TextComponent variant="h6" className="text-[#6B6B6B] mt-4 md:w-[444px]">
                    Please write something about yourself and let other members know why you're here. You can skip this section for now and complete this later in your settings under Edit info.
                </TextComponent>
            </Container>
            <Formik
                initialValues={initialValues}
                validationSchema={descriptionValidationSchema}
                validateOnBlur
                validateOnChange={false}
                onSubmit={handleSubmit}
            >
                {({ handleChange, handleBlur, values }) => (
                    <Form className="flex flex-col w-full text-start gap-5">
                        <Container className="w-full">
                            <Field
                                as="textarea"
                                id="description"
                                name="description"
                                className="outline-none border border-[#E6E6E6] p-3 h-32 resize-none w-full"
                                placeholder="About me"
                                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                                    if (submitError) {
                                        setSubmitError("");
                                    }
                                    handleChange(event);
                                }}
                                onBlur={handleBlur}
                                maxLength={maxLengthBIO}
                            />
                            <ErrorMessage name="description" component="div" className="text-red-500 text-xs" />
                            {submitError && (
                                <TextComponent variant="h7" className="text-red-500 text-xs mt-2">
                                    {submitError}
                                </TextComponent>
                            )}
                            <TextComponent variant="h6" className="text-right text-graySecondary">
                                {values.description?.length}/{maxLengthBIO}
                            </TextComponent>
                        </Container>
                        <CustomButton type="submit" >
                            Finish
                        </CustomButton>
                    </Form>
                )}
            </Formik>
        </Column>
        </Column>
    </LoadingComponent>
    );
}
